import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';
import { germanDateValidator, zipCodeValidators } from '@vydaps/gui-commons/utils';
import { FieldType } from '@ps-gui/shared-utils';

const composedZipCodeValidator = Validators.compose(zipCodeValidators())!;

export const createValidator =
  ({ validator, errorKey, message }: ValidationConfig): ValidatorFn =>
  (control) =>
    validator(control) === null ? null : { [errorKey]: message };

export interface ValidationConfig {
  validator: ValidatorFn;
  errorKey: string;
  message: string;
}

export type ValidationFactory<T = never, Q = never> =
  | (() => ValidationConfig)
  | ((arg1: T) => ValidationConfig)
  | ((arg1: T, arg2: Q) => ValidationConfig);

export const Validations = {
  required: () => ({
    validator: Validators.required,
    errorKey: 'required',
    message: 'Dies ist ein Pflichtfeld',
  }),
  email: () => ({
    validator: Validators.email,
    errorKey: 'invalidEmail',
    message: 'Das Feld entspricht keiner gängigen E-Mail-Adresse',
  }),
  zip: () => ({
    validator: composedZipCodeValidator,
    errorKey: 'invalidZip',
    message: 'Das Feld entspricht keiner gültigen deutschen Postleitzahl (01234)',
  }),
  date: () => ({
    validator: germanDateValidator,
    errorKey: 'invalidDate',
    message: 'Das Feld ist kein gültiges Datum',
  }),
  time: () => ({
    validator: Validators.pattern(/^([01]\d|2[0-3]):([0-5]\d)$/),
    errorKey: 'invalidTime',
    message: 'Das Feld ist keine gültige Uhrzeit',
  }),
  maxLength: (maxLength: number) => ({
    validator: Validators.maxLength(maxLength),
    errorKey: 'maxLength',
    message: `Das Feld darf maximal ${maxLength} Zeichen enthalten`,
  }),
  naturalNumber: () => ({
    validator: Validators.pattern(/^\d*$/),
    errorKey: 'invalidNumber',
    message: 'Das Feld muss eine positive Ganzzahl sein',
  }),
} satisfies Record<string, ValidationFactory>;

export const errorText = (control: AbstractControl, type?: FieldType) => {
  const errors = control.errors ?? {};

  // The used datepicker directive overwrites all set validators on the given form control
  // and adds a format error if the controls value is not a valid date.
  // To avoid this and still use our expected error message the format error
  // gets replaced with our default date error.
  if (type === FieldType.DATE && 'format' in errors) {
    delete errors['format'];
    errors['invalidDate'] = 'Das Feld ist kein gültiges Datum';
  }

  return Object.values(errors).join('\n');
};
